import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  Clock,
  MapPin,
  Star,
  FileText,
  Users,
  BarChart3,
  Share2,
  Link2,
  TicketPercent,
} from "lucide-react";
import { Button, Tabs, Spin, Tooltip } from "antd";
import { format } from "date-fns";
import { getPortalEventDetail } from "services/portalEvents.service";
import { renderErrorNotifications, renderSuccessNotifications } from "helpers/error.helpers";
import { getPrimaryMediaUrl } from "commons/helper";
import {
  getEventShareUrl,
  getShareCopiedNotification,
  SHOW_IN_LISTINGS_COPY,
} from "../commons/events.helpers";
import OverviewTab from "./_blocks/OverviewTab";
import AttendeesTab from "./_blocks/AttendeesTab";
import AnalyticsTab from "./_blocks/AnalyticsTab";
import DiscountCodesTab from "./_blocks/DiscountCodesTab";
import useDiscountCodesConfig from "hooks/useDiscountCodesConfig/useDiscountCodesConfig";

const STATUS_BG = {
  published: "bg-emerald-500",
  draft: "bg-amber-500",
  cancelled: "bg-red-500",
};

function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { enabled: codesEnabled } = useDiscountCodesConfig();

  useEffect(() => {
    async function fetchEvent() {
      const { data, errors } = await getPortalEventDetail(eventId);
      if (errors?.length) {
        renderErrorNotifications(errors);
        navigate("/shoup-community/events");
        return;
      }
      setEvent(data);
      setLoading(false);
    }
    if (eventId) fetchEvent();
  }, [eventId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  if (!event) return null;

  const statusKey =
    String(event.publication_status ?? "").toLowerCase() === "published"
      ? "published"
      : String(event.publication_status ?? "").toLowerCase() === "cancelled"
        ? "cancelled"
        : "draft";

  // Missing field (older API responses) means listed.
  const isLinkOnly = event.show_in_listings === false;
  const canShare = statusKey === "published" && Boolean(event.slug);
  const shareButton = (
    <Button
      type={statusKey === "published" && isLinkOnly ? "primary" : "default"}
      size="small"
      icon={<Share2 className="h-4 w-4" />}
      onClick={() => {
        navigator.clipboard.writeText(getEventShareUrl(event.slug));
        renderSuccessNotifications(
          getShareCopiedNotification(event.show_in_listings)
        );
      }}
      disabled={!canShare}
    >
      Share
    </Button>
  );

  const tabItems = [
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4" /> Overview
        </span>
      ),
      children: <OverviewTab event={event} />,
    },
    {
      key: "attendees",
      label: (
        <span className="flex items-center gap-2">
          <Users className="h-4 w-4" /> Attendees
        </span>
      ),
      children: <AttendeesTab eventId={eventId} isPublished={statusKey === "published"} event={event} />,
    },
    {
      key: "analytics",
      label: (
        <span className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> Analytics
        </span>
      ),
      children: <AnalyticsTab eventId={eventId} isPublished={statusKey === "published"} />,
    },
    // Hidden while the discount codes switch is off or unknown (ui-spec §0.1).
    ...(codesEnabled
      ? [
          {
            key: "discount-codes",
            label: (
              <span className="flex items-center gap-2">
                <TicketPercent className="h-4 w-4" /> Discount codes
              </span>
            ),
            children: (
              <DiscountCodesTab
                eventId={eventId}
                communityId={event.community_id}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button type="text" icon={<ChevronLeft className="h-4 w-4" />}>
          <Link to="/shoup-community/events">Back to Events</Link>
        </Button>
        <div className="flex items-center space-x-2">
          <Button type="default" size="small">
            <Link
              to={`/shoup-community/events/${eventId}/edit`}
              className="flex items-center"
            >
              <FileText className="mr-2 h-4 w-4" /> Edit
            </Link>
          </Button>
          {canShare ? (
            shareButton
          ) : (
            <Tooltip
              title={
                statusKey === "published"
                  ? SHOW_IN_LISTINGS_COPY.shareUnavailableTooltip
                  : SHOW_IN_LISTINGS_COPY.shareDisabledTooltip
              }
            >
              <span tabIndex={0} className="inline-flex">
                {shareButton}
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-64 md:h-80 overflow-hidden rounded-2xl border shadow-lg bg-muted">
        {event.media?.length ? (
          <img
            src={getPrimaryMediaUrl(event.media)}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-primary/5">
            <CalendarIcon className="h-20 w-20" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-8 left-8 text-white space-y-2">
          <div className="flex items-center space-x-2 flex-wrap gap-1">
            {event.event_type && (
              <span className="bg-primary px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                {event.event_type.replace("_", " ")}
              </span>
            )}
            <span
              className={`${STATUS_BG[statusKey]} px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider`}
            >
              {statusKey}
            </span>
            {isLinkOnly && (
              <span className="bg-slate-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                <Link2 className="h-3 w-3" aria-hidden />{" "}
                {SHOW_IN_LISTINGS_COPY.badge}
              </span>
            )}
            {/* Featured has no effect while link only, so hide the pill. */}
            {event.is_featured && !isLinkOnly && (
              <span className="bg-yellow-500 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                <Star className="h-3 w-3" /> Featured
              </span>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold">{event.title}</h1>
          <div className="flex items-center space-x-4 text-sm opacity-90 flex-wrap gap-y-1">
            {event.start_date_time && (
              <div className="flex items-center">
                <Clock className="mr-1.5 h-4 w-4" />
                {format(
                  new Date(event.start_date_time),
                  "MMM d, yyyy \u2022 h:mm a",
                )}
              </div>
            )}
            {event.location?.name && (
              <div className="flex items-center">
                <MapPin className="mr-1.5 h-4 w-4" />
                {event.location.name}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultActiveKey="overview" items={tabItems} />
    </div>
  );
}

export default EventDetail;
